/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useMemo, useState } from "react"
import BasicInput from "../../Component/Input/BasicInput"
import { verifyLength } from "../../Service/Helper/Utils"
import MainButton from "../../Component/Button/MainButton"
import { CatchLoginError } from "../../Service/Exceptions/exceptions"
import { useNavigate, useSearchParams } from "react-router-dom"
import UserService from "../../Service/API/UserService"
import LoadingScreen from "../../Component/Loader/LoadingScreen"
import { Button, Col, Row } from "reactstrap"
import { registerNewPayloads } from "../../Service/Variables/Common"
import swal from "sweetalert"

const SELF_REGISTRATION_MITRA = "0001"
const OSM_REVERSE_URL = "https://nominatim.openstreetmap.org/reverse"

const normalizeLocation = (value = "") => value
    .toLowerCase()
    .replace(/^(provinsi|province|daerah khusus|dki|di)\s+/g, "")
    .replace(/[^a-z0-9]+/g, "")

const pickAddressValue = (address = {}, keys = []) => {
    for (const key of keys) {
        if (address[key]) return address[key]
    }
    return ""
}

function RegisterNewPages() {
    const navigation = useNavigate()
    const [searchParams] = useSearchParams()
    const userService = useMemo(() => new UserService(), [])
    const selectedPackage = searchParams.get("package") || ""
    const selectedSpeed = searchParams.get("speed") || ""
    const selectedPrice = searchParams.get("price") || ""
    const packageNote = [selectedPackage, selectedSpeed, selectedPrice].filter(Boolean).join(" - ")

    const [isLoad, setIsLoad] = useState(false)
    const [locationLoading, setLocationLoading] = useState(false)
    const [locationMessage, setLocationMessage] = useState("")
    const [mitraOptions, setMitraOptions] = useState([])
    const [detectedProvince, setDetectedProvince] = useState("KEPULAUAN BANGKA BELITUNG")
    const [selectedMitra, setSelectedMitra] = useState(SELF_REGISTRATION_MITRA)
    const [firstname, setfirstname] = useState("")
    const [lastname, setlastname] = useState("")
    const [email, setemail] = useState("")
    const [ktp, setktp] = useState("")
    const [mobileNo, setmobileNo] = useState("")
    const [city, setcity] = useState("PANGKAL PINANG")
    const [itemNotes, setitemNotes] = useState(packageNote)
    const [subDistrict, setsubDistrict] = useState("BUKIT INTAN")
    const [urban, seturban] = useState("AIR ITAM")
    const [category, setcategory] = useState("BROADBAND")
    const [addressLine, setAddressLine] = useState("")
    const [latitude, setlatitude] = useState("0")
    const [longitude, setlongitude] = useState("0")

    const loadMitraOptions = useCallback(async () => {
        try {
            const result = await userService.getMitraLocation()
            setMitraOptions(result?.data ?? [])
        } catch (error) {
            setMitraOptions([])
        }
    }, [userService])

    useEffect(() => {
        loadMitraOptions()
    }, [loadMitraOptions])

    const findMitraByProvince = useCallback((provinceName) => {
        const normalizedProvince = normalizeLocation(provinceName)
        if (!normalizedProvince) return null

        return mitraOptions.find((option) => {
            const provinceLabel = String(option.label || "").split("-")[0]
            const normalizedOptionProvince = normalizeLocation(provinceLabel)
            return normalizedOptionProvince === normalizedProvince ||
                normalizedOptionProvince.includes(normalizedProvince) ||
                normalizedProvince.includes(normalizedOptionProvince)
        }) || null
    }, [mitraOptions])

    const applyOsmAddress = useCallback((address = {}, lat, lon, displayName = "") => {
        const province = pickAddressValue(address, ["state", "province", "region"])
        const detectedCity = pickAddressValue(address, ["city", "town", "municipality", "county", "regency"])
        const detectedDistrict = pickAddressValue(address, ["city_district", "district", "suburb", "county"])
        const detectedSubDistrict = pickAddressValue(address, ["village", "hamlet", "neighbourhood", "quarter", "suburb"])
        const detectedAddressLine = [
            pickAddressValue(address, ["road", "pedestrian", "footway", "residential"]),
            pickAddressValue(address, ["house_number", "building"])
        ].filter(Boolean).join(" ")
        const matchedMitra = findMitraByProvince(province)

        setDetectedProvince(province?.toUpperCase() ?? 'KEPULAUAN BANGKA BELITUNG')
        setcity(detectedCity || city?.toUpperCase() || 'PANGKAL PINANG')
        setsubDistrict(detectedDistrict || detectedSubDistrict?.toUpperCase() || "BUKIT INTAN")
        seturban(detectedSubDistrict || detectedDistrict?.toUpperCase() || "AIR ITAM")
        setAddressLine(displayName || detectedAddressLine || "")
        setlatitude(String(lat))
        setlongitude(String(lon))
        setSelectedMitra(matchedMitra?.value || SELF_REGISTRATION_MITRA)
        setLocationMessage(
            matchedMitra
                ? `Lokasi terdeteksi: ${province}. Area dalam coverage ${matchedMitra.label}.`
                : `Lokasi terdeteksi: ${province || "tidak diketahui"}. Area tidak dalam Coverage.`
        )
    }, [city, findMitraByProvince])

    useEffect(() => {
        if (!itemNotes) {
            navigation("/#paket-internet")
        }
    }, [itemNotes, navigation])

    const geolocate = async () => {
        if (!navigator.geolocation) {
            setLocationMessage("Lokasi Tidak Tersedia.")
            return
        }

        setLocationLoading(true)
        setLocationMessage("Mengambil lokasi perangkat...")
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude: lat, longitude: lon } = position.coords
            try {
                const params = new URLSearchParams({
                    format: "jsonv2",
                    addressdetails: "1",
                    lat: String(lat),
                    lon: String(lon),
                    "accept-language": "id"
                })
                const response = await fetch(`${OSM_REVERSE_URL}?${params.toString()}`)
                if (!response.ok) throw new Error("Gagal membaca lokasi dari OSM")
                const data = await response.json()
                applyOsmAddress(data.address || {}, lat, lon, data.display_name || "")
            } catch (error) {
                setlatitude(String(lat))
                setlongitude(String(lon))
                setSelectedMitra(SELF_REGISTRATION_MITRA)
                setLocationMessage(`${error.message}. Koordinat tersimpan, alamat bisa diisi manual.`)
            } finally {
                setLocationLoading(false)
            }
        }, (error) => {
            setLocationLoading(false)
            setLocationMessage(error?.message || "Izin lokasi ditolak. Isi alamat manual.")
        }, {
            enableHighAccuracy: true,
            timeout: 12000,
            maximumAge: 60000
        })
    }

    async function handleClick () {
        if (!firstname || !lastname || !email || !ktp || !mobileNo || !city || !subDistrict || !urban || !addressLine || !itemNotes) {
            window.alert("Lengkapi nama, email, KTP, WhatsApp, alamat, dan paket yang diminati.")
            return
        }

        try {
            setIsLoad(true)
            const payload = registerNewPayloads(
                firstname,
                lastname,
                selectedMitra || SELF_REGISTRATION_MITRA,
                email,
                ktp,
                mobileNo,
                city,
                itemNotes,
                subDistrict,
                urban,
                category,
                addressLine,
                latitude,
                longitude
            )

            const result = await userService.registerNew(payload)
            swal('Success', result?.message ?? "Registrasi berhasil dikirim", 'success')
            navigation("/login")
            setIsLoad(false)
        } catch (error) {
            setIsLoad(false)
            swal("Error", `Terjadi Kesalahan, Mohon Coba kembali`,"warning" )
            // return CatchLoginError(error, navigation)
        }
    }

    const selectedMitraOption = mitraOptions.find((option) => option.value === selectedMitra)
    const selectedMitraLabel = selectedMitraOption?.label || selectedMitra

    const children = (
        <main className="self-register-page">
            <section className="self-register-hero">
                <div className="landing-container self-register-grid">
                    <div className="self-register-copy">
                        <h1>Daftar Layanan Goodnet.</h1>
                        <p>Isi data dan lokasi. Tim kami akan menghubungi Anda untuk cek coverage, jadwal survey, dan konfirmasi paket.</p>
                        {/* <div className="self-register-steps">
                            <div><strong>1</strong><span>Pilih paket</span></div>
                            <div><strong>2</strong><span>Isi data lokasi</span></div>
                            <div><strong>3</strong><span>Tim cek coverage</span></div>
                        </div> */}
                    </div>

                    <div className="self-register-card">
                        <div className="self-register-card-head">
                            <div>
                                <span>Paket diminati</span>
                                <strong>{itemNotes || "Belum memilih paket"}</strong>
                            </div>
                            <button type="button" onClick={() => navigation("/#paket-internet")}>Ganti</button>
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <BasicInput
                                    placeholder="Nama depan"
                                    value={firstname}
                                    onChange={(e) => {
                                        if (!verifyLength(e.target.value, 32)) setfirstname(e.target.value)
                                    }}
                                />
                            </Col>
                            <Col md={6}>
                                <BasicInput
                                    placeholder="Nama belakang"
                                    value={lastname}
                                    onChange={(e) => {
                                        if (!verifyLength(e.target.value, 32)) setlastname(e.target.value)
                                    }}
                                />
                            </Col>
                            <Col md={6}>
                                <BasicInput
                                    placeholder="Email"
                                    value={email}
                                    type="email"
                                    onChange={(e) => setemail(e.target.value)}
                                />
                            </Col>
                            <Col md={6}>
                                <div className="self-register-area-note">
                                    <strong>{selectedMitra === SELF_REGISTRATION_MITRA ? "Area pendaftaran pusat" : "Area terdeteksi"}</strong>
                                    <span>
                                        {detectedProvince
                                            ? `${detectedProvince} - ${selectedMitra === SELF_REGISTRATION_MITRA ? "mitra pusat" : selectedMitraLabel}`
                                            : "Cek Coverage otomatis."}
                                    </span>
                                </div>
                            </Col>
                            <Col md={6}>
                                <BasicInput
                                    placeholder="No WhatsApp"
                                    value={mobileNo}
                                    onChange={(e) => {
                                        if (!verifyLength(e.target.value, 13)) setmobileNo(e.target.value)
                                    }}
                                type="number"
                                />
                            </Col>
                            <Col md={6}>
                                <BasicInput
                                    placeholder="Nomor KTP"
                                    value={ktp}
                                    onChange={(e) => {
                                        if (!verifyLength(e.target.value, 17)) setktp(e.target.value)
                                    }}
                                type="number"
                                />
                            </Col>
                            <Col md={4}>
                                <BasicInput placeholder="Kota" value={city} onChange={(e) => setcity(e.target.value.toUpperCase())} />
                            </Col>
                            <Col md={4}>
                                <BasicInput placeholder="Kecamatan" value={subDistrict} onChange={(e) => setsubDistrict(e.target.value.toUpperCase())} />
                            </Col>
                            <Col md={4}>
                                <BasicInput placeholder="Kelurahan" value={urban} onChange={(e) => seturban(e.target.value.toUpperCase())} />
                            </Col>
                            <Col md={12}>
                                <BasicInput
                                    placeholder="Alamat lengkap, nama jalan, nomor rumah, patokan"
                                    value={addressLine}
                                    onChange={(e) => {
                                        if (!verifyLength(e.target.value, 500)) setAddressLine(e.target.value)
                                    }}
                                />
                            </Col>
                            <Col md={12}>
                                <BasicInput
                                    placeholder="Paket yang diminati"
                                    value={itemNotes}
                                    disabled={true}
                                    onChange={(e) => setitemNotes(e.target.value)}
                                />
                            </Col>
                            <Col md={4}>
                                <BasicInput disabled placeholder="Kategori" value={category} onChange={(e) => setcategory(e.target.value)} />
                            </Col>
                            <Col md={12}>
                                <div className="self-register-location-panel">
                                    <div>
                                        {/* <strong>Isi alamat otomatis dari lokasi perangkat</strong> */}
                                        <span>{locationMessage || "."}</span>
                                    </div>
                                    <button type="button" onClick={geolocate} disabled={locationLoading}>
                                        <i className="fa fa-location-arrow" /> {locationLoading ? "Scanning..." : "Cek Ulang Lokasi"}
                                    </button>
                                </div>
                            </Col>
                        </Row>

                        <div className="self-register-actions">
                            <MainButton title="Daftar" onClick={handleClick} />
                            <Button type="button" onClick={() => navigation("/")}>Kembali</Button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    )

    return (
        <LoadingScreen
            loading={isLoad}
            children={children}
        />
    )
}

export default RegisterNewPages
